'use client';

import { c as _c } from "react/compiler-runtime";
import { type FundingProvider, formatFiatUnits, type OnrampClient } from '@morfi/onramp-sdk';
import { type FundingCheckout, useFundingProfile, useFundingProfileContact } from '@morfi/onramp-sdk/react';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { logger } from '@/lib/telemetry/logger';
import { useFundingStage } from '../shared/funding-chrome';
import { FundingLoading } from '../shared/funding-loading';
import { VerificationForm } from '../verification/funding-verification';
import { FundingDisconnect } from './funding-disconnect';
interface Props {
  client: OnrampClient;
  provider: FundingProvider;
  connection: FundingCheckout['profileConnection'];
  sessionProofId: string;
  currency: string;
  decimals: number;
  verificationError?: boolean;
  canDisconnect: boolean;
  disconnectBusy: boolean;
  onDisconnect(): Promise<boolean>;
  onBack(): void;
  onUpdated(): void;
  onVerify(): void;
}
export function FundingProfile(props) {
  const $ = _c(125);
  const t = useTranslations("onramp");
  const locale = useLocale();
  const [editing, setEditing] = useState(null);
  let t0;
  if ($[0] !== props.client || $[1] !== props.connection || $[2] !== props.currency || $[3] !== props.decimals || $[4] !== props.provider || $[5] !== props.sessionProofId) {
    t0 = {
      client: props.client,
      sessionProofId: props.sessionProofId,
      currency: props.currency,
      decimals: props.decimals,
      provider: props.provider,
      connection: props.connection
    };
    $[0] = props.client;
    $[1] = props.connection;
    $[2] = props.currency;
    $[3] = props.decimals;
    $[4] = props.provider;
    $[5] = props.sessionProofId;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  const profile = useFundingProfile(t0);
  let t1;
  if ($[7] !== profile.data?.limitsUnavailable || $[8] !== profile.error) {
    t1 = () => {
      if (profile.error || profile.data?.limitsUnavailable) {
        logger.warn("onramp.profile_failed", {
          failed: true,
          limitsUnavailable: Boolean(profile.data?.limitsUnavailable)
        });
      }
    };
    $[7] = profile.data?.limitsUnavailable;
    $[8] = profile.error;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const t2 = profile.data?.limitsUnavailable;
  let t3;
  if ($[10] !== profile.error || $[11] !== t2) {
    t3 = [profile.error, t2];
    $[10] = profile.error;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  useEffect(t1, t3);
  let t4;
  if ($[13] !== t) {
    t4 = t("profile_title");
    $[13] = t;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  useFundingStage(t4, false, props.onBack, !editing);
  if (editing) {
    let t5;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = () => setEditing(null);
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== editing || $[17] !== props.client || $[18] !== props.onUpdated) {
      t6 = <ProfileContact client={props.client} kind={editing} onBack={t5} onComplete={props.onUpdated} />;
      $[16] = editing;
      $[17] = props.client;
      $[18] = props.onUpdated;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    return t6;
  }
  if (profile.loading) {
    let t5;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <FundingLoading />;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    return t5;
  }
  if (profile.error || !profile.data) {
    let t5;
    if ($[21] !== t) {
      t5 = t("profile_load_error");
      $[21] = t;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    let t6;
    if ($[23] !== t5) {
      t6 = <p>{t5}</p>;
      $[23] = t5;
      $[24] = t6;
    } else {
      t6 = $[24];
    }
    const t7 = props.onUpdated;
    let t8;
    if ($[25] !== t) {
      t8 = t("retry");
      $[25] = t;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    let t9;
    if ($[27] !== props.onUpdated || $[28] !== t8) {
      t9 = <AnimatedButton onClick={t7}>{t8}</AnimatedButton>;
      $[27] = props.onUpdated;
      $[28] = t8;
      $[29] = t9;
    } else {
      t9 = $[29];
    }
    let t10;
    if ($[30] !== t6 || $[31] !== t9) {
      t10 = <div role="alert" className="flex flex-col gap-3">{t6}{t9}</div>;
      $[30] = t6;
      $[31] = t9;
      $[32] = t10;
    } else {
      t10 = $[32];
    }
    return t10;
  }
  const {
    payer,
    verification
  } = profile.data;
  const presentation = profile.presentation;
  let t5;
  if ($[33] !== locale || $[34] !== props.currency || $[35] !== props.decimals) {
    t5 = units => formatFiatUnits(units, props.decimals, props.currency, locale);
    $[33] = locale;
    $[34] = props.currency;
    $[35] = props.decimals;
    $[36] = t5;
  } else {
    t5 = $[36];
  }
  const money = t5;
  let t6;
  if ($[37] !== props.verificationError || $[38] !== t) {
    t6 = props.verificationError && <p role="alert" className="text-caption text-foreground-secondary">{t("profile_load_error")}</p>;
    $[37] = props.verificationError;
    $[38] = t;
    $[39] = t6;
  } else {
    t6 = $[39];
  }
  const t7 = profile.data.guest ? "profile_guest" : "profile_connected";
  let t8;
  if ($[40] !== t || $[41] !== t7) {
    t8 = t(t7);
    $[40] = t;
    $[41] = t7;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  let t9;
  if ($[43] !== t8) {
    t9 = <h3 className="text-heading-sm">{t8}</h3>;
    $[43] = t8;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  let t10;
  if ($[45] !== t) {
    t10 = t("email_address");
    $[45] = t;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  let t11;
  if ($[47] !== payer.verifiedEmail || $[48] !== t) {
    t11 = payer.verifiedEmail ?? t("profile_not_verified");
    $[47] = payer.verifiedEmail;
    $[48] = t;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== t) {
    t12 = t("change_email");
    $[50] = t;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  let t13;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setEditing("email");
    $[52] = t13;
  } else {
    t13 = $[52];
  }
  let t14;
  if ($[53] !== t10 || $[54] !== t11 || $[55] !== t12) {
    t14 = <ProfileContactRow label={t10} value={t11} action={t12} onEdit={t13} />;
    $[53] = t10;
    $[54] = t11;
    $[55] = t12;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== t) {
    t15 = t("phone_number");
    $[57] = t;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] !== payer.verifiedPhone || $[60] !== t) {
    t16 = payer.verifiedPhone ?? t("profile_not_verified");
    $[59] = payer.verifiedPhone;
    $[60] = t;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  let t17;
  if ($[62] !== t) {
    t17 = t("change_phone");
    $[62] = t;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setEditing("phone");
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== t15 || $[66] !== t16 || $[67] !== t17) {
    t19 = <ProfileContactRow label={t15} value={t16} action={t17} onEdit={t18} />;
    $[65] = t15;
    $[66] = t16;
    $[67] = t17;
    $[68] = t19;
  } else {
    t19 = $[68];
  }
  let t20;
  if ($[69] !== t14 || $[70] !== t19) {
    t20 = <div className="flex flex-col gap-3">{t14}{t19}</div>;
    $[69] = t14;
    $[70] = t19;
    $[71] = t20;
  } else {
    t20 = $[71];
  }
  let t21;
  if ($[72] !== t) {
    t21 = t("profile_kyc_title");
    $[72] = t;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  let t22;
  if ($[74] !== t21) {
    t22 = <h3 className="text-heading-sm">{t21}</h3>;
    $[74] = t21;
    $[75] = t22;
  } else {
    t22 = $[75];
  }
  let t23;
  if ($[76] !== t || $[77] !== verification) {
    t23 = verification ? verification.status === "active" ? null : <p>{t(`profile_status_${verification.status}`)}</p> : <p className="text-caption text-foreground-secondary">{t("profile_stepup_unknown")}</p>;
    $[76] = t;
    $[77] = verification;
    $[78] = t23;
  } else {
    t23 = $[78];
  }
  let t24;
  if ($[79] !== presentation.requirements || $[80] !== t) {
    let t25;
    if ($[82] !== t) {
      t25 = requirement => <div key={requirement.category} className="flex items-center justify-between gap-3"><span className="text-caption">{t(`profile_requirement_${requirement.category}`)}</span><span className="text-caption text-foreground-secondary">{t(`profile_requirement_status_${requirement.status}`)}</span></div>;
      $[82] = t;
      $[83] = t25;
    } else {
      t25 = $[83];
    }
    t24 = presentation.requirements.map(t25);
    $[79] = presentation.requirements;
    $[80] = t;
    $[81] = t24;
  } else {
    t24 = $[81];
  }
  let t25;
  if ($[84] !== presentation.canVerify || $[85] !== profile.data.canConnect || $[86] !== props.onVerify || $[87] !== t) {
    t25 = presentation.canVerify && <><AnimatedButton onClick={props.onVerify}>{profile.data?.canConnect ? t("profile_connect_moonpay") : t("profile_complete_verification")}</AnimatedButton><p className="text-caption text-foreground-secondary">{t("profile_higher_limits")}</p></>;
    $[84] = presentation.canVerify;
    $[85] = profile.data.canConnect;
    $[86] = props.onVerify;
    $[87] = t;
    $[88] = t25;
  } else {
    t25 = $[88];
  }
  let t26;
  if ($[89] !== t22 || $[90] !== t23 || $[91] !== t24 || $[92] !== t25) {
    t26 = <div className="flex flex-col gap-3">{t22}{t23}{t24}{t25}</div>;
    $[89] = t22;
    $[90] = t23;
    $[91] = t24;
    $[92] = t25;
    $[93] = t26;
  } else {
    t26 = $[93];
  }
  let t27;
  if ($[94] !== t) {
    t27 = t("profile_limits_title");
    $[94] = t;
    $[95] = t27;
  } else {
    t27 = $[95];
  }
  let t28;
  if ($[96] !== t27) {
    t28 = <h3 className="text-heading-sm">{t27}</h3>;
    $[96] = t27;
    $[97] = t28;
  } else {
    t28 = $[97];
  }
  let t29;
  if ($[98] !== money || $[99] !== presentation.limits || $[100] !== t) {
    t29 = presentation.limits.length ? <><p className="text-caption text-foreground-secondary">{t("profile_limits_quote")}</p>{presentation.limits.map(limit => <div key={limit.period} className="flex flex-col gap-1"><p className="text-caption">{t(`limit_${limit.period}`)}</p><p className="text-caption text-foreground-secondary">{t("profile_limit_amount", {
            remaining: money(limit.remainingUnits),
            total: money(limit.limitUnits)
          })}</p></div>)}</> : <p className="text-caption text-foreground-secondary">{t("profile_limits_unavailable")}</p>;
    $[98] = money;
    $[99] = presentation.limits;
    $[100] = t;
    $[101] = t29;
  } else {
    t29 = $[101];
  }
  let t30;
  if ($[102] !== t28 || $[103] !== t29) {
    t30 = <div className="flex flex-col gap-3">{t28}{t29}</div>;
    $[102] = t28;
    $[103] = t29;
    $[104] = t30;
  } else {
    t30 = $[104];
  }
  const t31 = props.onUpdated;
  let t32;
  if ($[105] !== t) {
    t32 = t("profile_refresh");
    $[105] = t;
    $[106] = t32;
  } else {
    t32 = $[106];
  }
  let t33;
  if ($[107] !== props.onUpdated || $[108] !== t32) {
    t33 = <AnimatedButton variant="outline" onClick={t31}>{t32}</AnimatedButton>;
    $[107] = props.onUpdated;
    $[108] = t32;
    $[109] = t33;
  } else {
    t33 = $[109];
  }
  let t34;
  if ($[110] !== payer.verifiedEmail || $[111] !== payer.verifiedPhone || $[112] !== props.canDisconnect || $[113] !== props.connection || $[114] !== props.disconnectBusy || $[115] !== props.onDisconnect) {
    t34 = payer.verifiedEmail || payer.verifiedPhone || props.connection.verification ? <FundingDisconnect canDisconnect={props.canDisconnect} busy={props.disconnectBusy} onDisconnect={props.onDisconnect} /> : null;
    $[110] = payer.verifiedEmail;
    $[111] = payer.verifiedPhone;
    $[112] = props.canDisconnect;
    $[113] = props.connection;
    $[114] = props.disconnectBusy;
    $[115] = props.onDisconnect;
    $[116] = t34;
  } else {
    t34 = $[116];
  }
  let t35;
  if ($[117] !== t20 || $[118] !== t26 || $[119] !== t30 || $[120] !== t33 || $[121] !== t34 || $[122] !== t6 || $[123] !== t9) {
    t35 = <div className="flex flex-col gap-5" data-private="true">{t6}{t9}{t20}{t26}{t30}{t33}{t34}</div>;
    $[117] = t20;
    $[118] = t26;
    $[119] = t30;
    $[120] = t33;
    $[121] = t34;
    $[122] = t6;
    $[123] = t9;
    $[124] = t35;
  } else {
    t35 = $[124];
  }
  return t35;
}
function ProfileContactRow(t0) {
  const $ = _c(13);
  const {
    label,
    value,
    action,
    onEdit
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <p className="text-caption text-foreground-secondary">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <p className="break-all text-caption">{value}</p>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="min-w-0">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== action || $[8] !== onEdit) {
    t4 = <AnimatedButton variant="tertiary" size="sm" className="shrink-0 underline" onClick={onEdit}>{action}</AnimatedButton>;
    $[7] = action;
    $[8] = onEdit;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="flex items-center justify-between gap-3">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function ProfileContact(t0) {
  const $ = _c(7);
  const {
    client,
    kind,
    onBack,
    onComplete
  } = t0;
  const verification = useFundingProfileContact(client, kind);
  const completed = useRef(false);
  let t1;
  let t2;
  if ($[0] !== onComplete || $[1] !== verification.step) {
    t1 = () => {
      if (verification.step === "ready" && !completed.current) {
        completed.current = true;
        onComplete();
      }
    };
    t2 = [verification.step, onComplete];
    $[0] = onComplete;
    $[1] = verification.step;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onBack || $[5] !== verification) {
    t3 = <VerificationForm verification={verification} requireConsent={false} onBack={onBack} />;
    $[4] = onBack;
    $[5] = verification;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}